import { createEffect, createSignal, For, Show } from 'solid-js'
import Footer from '/web/shared/aifans/Footer'
import { AccountLayout } from '/web/shared/aifans/AccountLayout'
import { chatStore, toastStore } from '/web/store'
import { AIFansSubscription, SubscriptionEvent } from '/common/types/aifans-schema'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { useNavigate, useParams, A } from '@solidjs/router'
import CancellationModal from './CancellationModal'
import ReactivationModal from './ReactivationAdminModal'
import { formatTransactionType } from '/web/shared/aifans/lib/transaction-util'
import { getModelUrl } from '/web/shared/aifans/lib/model-util'

export default function AdminSubscriptions() {
  const [tableData, setTableData] = createSignal<AIFansSubscription[]>([])
  const [totalPages, setTotalPages] = createSignal(1)
  const [sortKey, setSortKey] = createSignal<keyof AIFansSubscription | null>(null)
  const [sortDirection, setSortDirection] = createSignal<'asc' | 'desc'>('desc')
  const [showCancellationModal, setShowCancellationModal] = createSignal(false)
  const [cancellingCharId, setCancellingCharId] = createSignal<string>()
  const [cancellingId, setCancellingId] = createSignal<string>()
  const [showReactivateModal, setShowReactivateModal] = createSignal(false)
  const [reactivatingId, setReactivatingId] = createSignal<string>('')
  const [isReactivating, setIsReactivating] = createSignal(false)
  const [userId, setUserId] = createSignal<string>('')
  const [expandedRows, setExpandedRows] = createSignal<Set<string>>(new Set())
  const [subscriptionEvents, setSubscriptionEvents] = createSignal<
    Record<string, SubscriptionEvent[]>
  >({})
  const [loadingEvents, setLoadingEvents] = createSignal<Set<string>>(new Set())

  const params = useParams()
  const navigate = useNavigate()
  const pageSize = 20

  const characterFromId = (characterId: string) => {
    const char = chatStore().allChars.list.find((c) => c._id === characterId)
    return char
  }

  createEffect(async () => {
    const currentPage = Number(params.page) || 1
    await loadData(currentPage)
  })

  const loadData = async (page: number) => {
    try {
      const res = await aifansSubscriptionApi.getAdminSubscriptions(page, pageSize)
      if (res.result) {
        if (sortKey()) {
          const sorted = getSortedData(res.result.subscriptions, sortKey()!, sortDirection())
          setTableData(sorted)
        } else {
          setTableData(res.result.subscriptions)
        }
        setTotalPages(Math.ceil(res.result.totalCount / pageSize))
      }
    } catch (error) {
      console.error(error)
      toastStore.error('Failed to load subscriptions')
    }
  }

  const changePage = (page: number) => {
    navigate(page === 1 ? '/admin/subscriptions' : `/admin/subscriptions/${page}`)
  }

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(dateString))
  }

  const cancelSubscription = async (characterId: string, userId: string) => {
    setCancellingCharId(characterId)
    setShowCancellationModal(true)
    setUserId(userId)
  }

  const handleCancellationConfirm = async (reason: string) => {
    const characterId = cancellingCharId()
    if (!characterId) return

    setCancellingId(characterId)
    try {
      const res = await aifansSubscriptionApi.cancelAIFansAdminSubscription(
        characterId,
        userId(),
        reason
      )
      if (res.result?.success) {
        await loadData(Number(params.page) || 1)
        toastStore.success('Subscription cancelled!')
      } else {
        throw new Error('Failed to cancel subscription')
      }
    } catch (error) {
      console.error(error)
      toastStore.error('Failed to cancel subscription')
    } finally {
      setCancellingId(undefined)
      setUserId('')
      setCancellingCharId(undefined)
      setShowCancellationModal(false)
    }
  }

  const reactivateSubscription = async (characterId: string, userId: string) => {
    setReactivatingId(characterId)
    setShowReactivateModal(true)
    setUserId(userId)
  }

  const resetModal = () => {
    setShowReactivateModal(false)
    setReactivatingId('')
    setUserId('')
  }

  const confirmReactivation = async () => {
    const characterId = reactivatingId()
    if (!characterId) return

    setIsReactivating(true)
    try {
      const res = await aifansSubscriptionApi.reactivateAdminSubscription(characterId, userId())
      if (res.result?.success) {
        await loadData(Number(params.page) || 1)
        toastStore.success('Subscription reactivated!')
      } else {
        throw new Error('Failed to reactivate subscription')
      }
    } catch (error) {
      console.error(error)
      toastStore.error('Failed to reactivate subscription')
    } finally {
      setIsReactivating(false)
      resetModal()
    }
  }

  const toggleRow = async (subscriptionId: string) => {
    const newSet = new Set(expandedRows())
    if (newSet.has(subscriptionId)) {
      newSet.delete(subscriptionId)
    } else {
      newSet.add(subscriptionId)
      if (!subscriptionEvents()[subscriptionId]) {
        setLoadingEvents((prev) => new Set(prev).add(subscriptionId))
        try {
          const { result } = await aifansSubscriptionApi.getSubscriptionEvents(subscriptionId)
          if (result) {
            setSubscriptionEvents((prev) => ({ ...prev, [subscriptionId]: result.result }))
          }
        } catch (error) {
          console.error(error)
          toastStore.error('Failed to load subscription events')
        } finally {
          setLoadingEvents((prev) => {
            const next = new Set(prev)
            next.delete(subscriptionId)
            return next
          })
        }
      }
    }
    setExpandedRows(newSet)
  }

  const eventLabelClass =
    'font-rajdhani text-[9px] font-bold uppercase tracking-[1.2px] text-[rgba(255,255,255,0.45)]'
  const eventValueClass = 'font-rajdhani text-[12px] text-[rgba(255,255,255,0.7)]'

  const renderTableRow = (sub: AIFansSubscription) => {
    const isExpanded = () => expandedRows().has(sub._id)
    const events = () => subscriptionEvents()[sub._id] || []
    const char = characterFromId(sub.characterId)
    const status = getStatus(sub)

    return (
      <>
        <tr
          style={{ 'border-bottom': '1px solid rgba(255,255,255,0.06)' }}
          class="transition-colors hover:bg-[rgba(138,43,226,0.05)]"
          data-subscription-id={sub._id}
        >
          <td class={tdClass} data-user-id={sub.userId}>
            <div class="flex items-start gap-2">
              <button
                onClick={() => toggleRow(sub._id)}
                class="mt-0.5 text-[10px] text-[rgba(255,255,255,0.4)] transition-colors hover:text-[var(--cf-cyan)]"
              >
                {isExpanded() ? '▼' : '▶'}
              </button>
              <span class="break-all">{sub.username}</span>
            </div>
          </td>
          <td class={tdClass} data-character-id={sub.characterId}>
            <Show when={char}>
              <A href={getModelUrl(char!.categorySlug, char!.slug)} class="hover:underline">
                <div class="font-bebas text-[16px] tracking-[0.5px] text-white lg:text-[19px] lg:tracking-[0.6px]">
                  {char!.name}
                </div>
                {char!.realName && (
                  <div class="mt-0.5 font-rajdhani text-[11px] font-semibold text-[rgba(255,255,255,0.35)] lg:mt-1 lg:text-[13px]">
                    {char!.realName}
                  </div>
                )}
              </A>
            </Show>
          </td>
          <td class={tdClass}>{formatDate(sub.startDate)}</td>
          <td class={tdClass}>{formatDate(sub.endDate)}</td>
          <td class={tdClass}>${sub.amount}</td>
          <td class={tdClass}>{sub.tokens}</td>
          <td class={tdClass}>
            <span
              class={`inline-block rounded-md border px-3 py-1 font-rajdhani text-[10px] font-bold uppercase tracking-[0.8px] lg:px-3.5 lg:py-1.5 lg:text-[11px] ${status.style}`}
            >
              {status.text}
            </span>
          </td>
          <td class={tdClass}>
            <Show when={sub.status === 'cancelled' || sub.status === 'reactivating'}>
              <button
                class="rounded-lg px-3.5 py-1.5 font-rajdhani text-[11px] font-bold uppercase tracking-[0.5px] text-white transition-all hover:-translate-y-0.5 lg:px-4 lg:py-2 lg:text-[13px]"
                style={{
                  background: 'linear-gradient(135deg, #8A2BE2, #FF3CAC)',
                  'box-shadow': '0 4px 14px rgba(138,43,226,0.35)',
                }}
                onClick={() => reactivateSubscription(sub.characterId, sub.userId)}
              >
                Activate
              </button>
            </Show>
            <Show when={sub.status === 'active'}>
              <button
                class="rounded-lg border border-[rgba(255,100,100,0.25)] bg-[rgba(255,100,100,0.06)] px-3.5 py-1.5 font-rajdhani text-[11px] font-bold uppercase tracking-[0.5px] text-[rgba(255,100,100,0.75)] transition-colors hover:bg-[rgba(255,100,100,0.18)] hover:text-[#ff6b6b] lg:px-4 lg:py-2 lg:text-[13px]"
                onClick={() => cancelSubscription(sub.characterId, sub.userId)}
                disabled={cancellingId() === sub.characterId}
              >
                {cancellingId() === sub.characterId ? (
                  <span class="flex items-center gap-2">
                    <div class="h-4 w-4 animate-spin rounded-full border-2 border-[rgba(255,100,100,0.75)] border-t-transparent"></div>
                    Cancelling...
                  </span>
                ) : (
                  'Cancel'
                )}
              </button>
            </Show>
          </td>
        </tr>
        <Show when={isExpanded()}>
          <tr>
            <td colspan="8" class="p-4" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <Show
                when={!loadingEvents().has(sub._id)}
                fallback={
                  <div class="flex justify-center py-4">
                    <div
                      class="h-6 w-6 animate-spin rounded-full border-2 border-t-transparent"
                      style={{
                        'border-color': 'var(--cf-cyan)',
                        'border-top-color': 'transparent',
                      }}
                    ></div>
                  </div>
                }
              >
                <Show
                  when={events().length > 0}
                  fallback={
                    <div class="py-4 text-center font-rajdhani text-[13px] text-[rgba(255,255,255,0.4)]">
                      No events found
                    </div>
                  }
                >
                  <table class="w-full border-collapse">
                    <thead>
                      <tr style={{ 'border-bottom': '1px solid rgba(255,255,255,0.08)' }}>
                        <th class={`px-3 py-2 text-left ${eventLabelClass}`}>Date</th>
                        <th class={`px-3 py-2 text-left ${eventLabelClass}`}>Type</th>
                        <th class={`px-3 py-2 text-left ${eventLabelClass}`}>Amount</th>
                        <th class={`px-3 py-2 text-left ${eventLabelClass}`}>Status</th>
                        <th class={`px-3 py-2 text-left ${eventLabelClass}`}>Method</th>
                        <th class={`px-3 py-2 text-left ${eventLabelClass}`}>Member ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={events()}>
                        {(event) => (
                          <tr
                            style={{ 'border-bottom': '1px solid rgba(255,255,255,0.04)' }}
                            data-transaction-id={event._id}
                          >
                            <td class={`px-3 py-2 ${eventValueClass}`}>{formatDate(event.date)}</td>
                            <td class={`px-3 py-2 ${eventValueClass}`}>
                              {formatTransactionType(event.type)}
                            </td>
                            <td class={`px-3 py-2 ${eventValueClass}`}>
                              {event.amount ? `$${event.amount}` : '-'}
                            </td>
                            <td class={`px-3 py-2 ${eventValueClass}`}>
                              {event.status || event.reasonDescription || '-'}
                            </td>
                            <td class={`px-3 py-2 ${eventValueClass}`}>
                              {(event.paymentType &&
                                `${event.paymentType} - ${event.paymentSubtype}`) ||
                                '-'}
                            </td>
                            <td class={`px-3 py-2 ${eventValueClass}`}>{event.memberId || '-'}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </Show>
              </Show>
            </td>
          </tr>
        </Show>
      </>
    )
  }

  const renderMobileCard = (sub: AIFansSubscription) => {
    const isExpanded = () => expandedRows().has(sub._id)
    const events = () => subscriptionEvents()[sub._id] || []
    const char = characterFromId(sub.characterId)
    const status = getStatus(sub)

    return (
      <div
        class="rounded-xl border border-[rgba(255,255,255,0.06)] p-4"
        style={{ background: 'rgba(255,255,255,0.02)' }}
      >
        {/* User — primary full-width row with expand toggle */}
        <div class="mb-2.5 flex items-start gap-2 border-b border-[rgba(255,255,255,0.06)] pb-2.5">
          <button
            onClick={() => toggleRow(sub._id)}
            class="mt-0.5 text-[11px] text-[rgba(255,255,255,0.4)] transition-colors hover:text-[var(--cf-cyan)]"
          >
            {isExpanded() ? '▼' : '▶'}
          </button>
          <div class="min-w-0 flex-1">
            <div class={eventLabelClass}>User</div>
            <div class="mt-0.5 break-all font-rajdhani text-[13px] text-[rgba(255,255,255,0.9)]">
              {sub.username}
            </div>
          </div>
        </div>

        {/* Character */}
        <div class="mb-2.5">
          <div class={eventLabelClass}>Character</div>
          <Show when={char}>
            <A href={getModelUrl(char!.categorySlug, char!.slug)} class="hover:underline">
              <div class="mt-0.5 font-bebas text-[16px] tracking-[0.5px] text-white">
                {char!.name}
              </div>
              {char!.realName && (
                <div class="mt-0.5 font-rajdhani text-[11px] font-semibold text-[rgba(255,255,255,0.35)]">
                  {char!.realName}
                </div>
              )}
            </A>
          </Show>
        </div>

        {/* Compact fields */}
        <div class="grid grid-cols-2 gap-x-3.5 gap-y-2.5">
          <div>
            <div class={eventLabelClass}>Start Date</div>
            <div class="mt-0.5 font-rajdhani text-[13px] text-[rgba(255,255,255,0.78)]">
              {formatDate(sub.startDate)}
            </div>
          </div>
          <div>
            <div class={eventLabelClass}>End Date</div>
            <div class="mt-0.5 font-rajdhani text-[13px] text-[rgba(255,255,255,0.78)]">
              {formatDate(sub.endDate)}
            </div>
          </div>
          <div>
            <div class={eventLabelClass}>Amount</div>
            <div class="mt-0.5 font-rajdhani text-[13px] text-[rgba(255,255,255,0.78)]">
              ${sub.amount}
            </div>
          </div>
          <div>
            <div class={eventLabelClass}>Tokens</div>
            <div class="mt-0.5 font-rajdhani text-[13px] text-[rgba(255,255,255,0.78)]">
              {sub.tokens}
            </div>
          </div>

          {/* Status */}
          <div class="col-span-2">
            <div class={eventLabelClass}>Status</div>
            <span
              class={`mt-1 inline-block rounded-md border px-2.5 py-0.5 font-rajdhani text-[10px] font-bold uppercase tracking-[0.8px] ${status.style}`}
            >
              {status.text}
            </span>
          </div>

          {/* Action — full-width row */}
          <div class="col-span-2 pt-1">
            <Show when={sub.status === 'cancelled' || sub.status === 'reactivating'}>
              <button
                class="w-full rounded-lg py-2.5 text-center font-rajdhani text-[11px] font-bold uppercase tracking-[0.5px] text-white"
                style={{ background: 'linear-gradient(135deg, #8A2BE2, #FF3CAC)' }}
                onClick={() => reactivateSubscription(sub.characterId, sub.userId)}
              >
                Activate
              </button>
            </Show>
            <Show when={sub.status === 'active'}>
              <button
                class="flex w-full items-center justify-center rounded-lg border border-[rgba(255,100,100,0.25)] bg-[rgba(255,100,100,0.06)] py-2.5 text-center font-rajdhani text-[11px] font-bold uppercase tracking-[0.5px] text-[rgba(255,100,100,0.75)] transition-colors hover:bg-[rgba(255,100,100,0.15)]"
                onClick={() => cancelSubscription(sub.characterId, sub.userId)}
                disabled={cancellingId() === sub.characterId}
              >
                {cancellingId() === sub.characterId ? (
                  <span class="flex items-center gap-2">
                    <div class="h-4 w-4 animate-spin rounded-full border-2 border-[rgba(255,100,100,0.75)] border-t-transparent"></div>
                    Cancelling...
                  </span>
                ) : (
                  'Cancel'
                )}
              </button>
            </Show>
          </div>
        </div>

        {/* Expandable events — compact stacked list */}
        <Show when={isExpanded()}>
          <div class="mt-3 border-t border-[rgba(255,255,255,0.06)] pt-3">
            <Show
              when={!loadingEvents().has(sub._id)}
              fallback={
                <div class="flex justify-center py-3">
                  <div
                    class="h-5 w-5 animate-spin rounded-full border-2 border-t-transparent"
                    style={{ 'border-color': 'var(--cf-cyan)', 'border-top-color': 'transparent' }}
                  ></div>
                </div>
              }
            >
              <Show
                when={events().length > 0}
                fallback={
                  <div class="py-3 text-center font-rajdhani text-[12px] text-[rgba(255,255,255,0.4)]">
                    No events found
                  </div>
                }
              >
                <div class="flex flex-col gap-2.5">
                  <For each={events()}>
                    {(event) => (
                      <div
                        class="rounded-lg border border-[rgba(255,255,255,0.05)] p-2.5"
                        style={{ background: 'rgba(255,255,255,0.02)' }}
                        data-transaction-id={event._id}
                      >
                        <div class="grid grid-cols-2 gap-x-3 gap-y-1.5">
                          <div>
                            <div class={eventLabelClass}>Date</div>
                            <div class={eventValueClass}>{formatDate(event.date)}</div>
                          </div>
                          <div>
                            <div class={eventLabelClass}>Type</div>
                            <div class={eventValueClass}>{formatTransactionType(event.type)}</div>
                          </div>
                          <div>
                            <div class={eventLabelClass}>Amount</div>
                            <div class={eventValueClass}>
                              {event.amount ? `$${event.amount}` : '-'}
                            </div>
                          </div>
                          <div>
                            <div class={eventLabelClass}>Status</div>
                            <div class={eventValueClass}>
                              {event.status || event.reasonDescription || '-'}
                            </div>
                          </div>
                          <div>
                            <div class={eventLabelClass}>Method</div>
                            <div class={eventValueClass}>
                              {(event.paymentType &&
                                `${event.paymentType} - ${event.paymentSubtype}`) ||
                                '-'}
                            </div>
                          </div>
                          <div>
                            <div class={eventLabelClass}>Member ID</div>
                            <div class={`break-all ${eventValueClass}`}>
                              {event.memberId || '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </Show>
          </div>
        </Show>
      </div>
    )
  }

  // Sorting logic
  const sortData = (key: keyof AIFansSubscription, initialDirection?: 'asc' | 'desc') => {
    if (sortKey() === key) {
      setSortDirection(sortDirection() === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection(initialDirection || 'asc')
    }

    const sorted = getSortedData(tableData(), key, sortDirection())
    setTableData(sorted)
  }

  const getSortedData = (
    data: AIFansSubscription[],
    key: keyof AIFansSubscription,
    direction: 'asc' | 'desc'
  ) => {
    return [...data].sort((a, b) => {
      let aValue: any = a[key]
      let bValue: any = b[key]

      if (key === 'amount') {
        aValue = parseFloat(a.amount.toString())
        bValue = parseFloat(b.amount.toString())
        if (isNaN(aValue)) aValue = Infinity
        if (isNaN(bValue)) bValue = Infinity
      }

      if (key === 'startDate' || key === 'endDate') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      }

      if (key === 'status') {
        aValue = a.status
        bValue = b.status
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1
      if (aValue > bValue) return direction === 'asc' ? 1 : -1
      return 0
    })
  }

  const renderSortIndicator = (key: keyof AIFansSubscription) => {
    if (sortKey() === key) {
      return (
        <span class="text-[10px] text-[var(--cf-cyan)]">
          {sortDirection() === 'asc' ? '▲' : '▼'}
        </span>
      )
    }
    return null
  }

  const getStatus = (subscription: AIFansSubscription) => {
    switch (subscription.status) {
      case 'active':
        return {
          text: 'Active',
          style: 'bg-[rgba(45,198,83,0.15)] text-[#2dc653] border-[rgba(45,198,83,0.3)]',
        }
      case 'cancelled':
        return {
          text: 'Cancelled',
          style: 'bg-[rgba(255,100,100,0.12)] text-[#ff6b6b] border-[rgba(255,100,100,0.25)]',
        }
      case 'expired':
        return {
          text: 'Expired',
          style: 'bg-[rgba(255,100,100,0.12)] text-[#ff6b6b] border-[rgba(255,100,100,0.25)]',
        }
      case 'reactivating':
        return {
          text: 'Reactivating...',
          style: 'bg-[rgba(255,215,0,0.12)] text-[#FFD700] border-[rgba(255,215,0,0.25)]',
        }
      case 'restarted':
        return {
          text: 'Restarted',
          style:
            'bg-[rgba(255,255,255,0.06)] text-[rgba(255,255,255,0.5)] border-[rgba(255,255,255,0.1)]',
        }
      default:
        return {
          text: 'Unknown',
          style:
            'bg-[rgba(255,255,255,0.06)] text-[rgba(255,255,255,0.5)] border-[rgba(255,255,255,0.1)]',
        }
    }
  }

  const renderPagination = () => {
    const current = Number(params.page || '1')
    const total = totalPages()
    const delta = 2
    const range: (number | string)[] = []

    range.push(1)
    let start = Math.max(2, current - delta)
    let end = Math.min(total - 1, current + delta)

    if (current - delta <= 2) {
      end = Math.min(total - 1, 1 + 2 * delta)
    }
    if (current + delta >= total - 1) {
      start = Math.max(2, total - 2 * delta)
    }

    if (start > 2) range.push('...')
    for (let i = start; i <= end; i++) {
      range.push(i)
    }
    if (end < total - 1) range.push('...')
    if (total > 1) range.push(total)

    const numBase = 'rounded-lg px-3 py-1.5 font-rajdhani text-xs font-bold transition-colors'
    const inactive =
      'bg-[rgba(255,255,255,0.06)] text-[rgba(255,255,255,0.5)] hover:bg-[rgba(255,255,255,0.1)]'

    return (
      <div class="flex items-center justify-center gap-2 border-t border-[rgba(255,255,255,0.06)] p-4">
        <button
          onClick={() => current > 1 && changePage(current - 1)}
          disabled={current === 1}
          class={`${numBase} ${inactive} ${current === 1 ? 'cursor-not-allowed opacity-40' : ''}`}
        >
          ←
        </button>

        <For each={range}>
          {(page) => {
            if (page === '...') {
              return (
                <span class="px-2 py-1.5 font-rajdhani text-xs text-[rgba(255,255,255,0.35)]">
                  ...
                </span>
              )
            }
            return (
              <button
                onClick={() => changePage(page as number)}
                class={`${numBase} ${current === page ? 'bg-cf-primary text-white' : inactive}`}
              >
                {page}
              </button>
            )
          }}
        </For>

        <button
          onClick={() => current < total && changePage(current + 1)}
          disabled={current === total}
          class={`${numBase} ${inactive} ${
            current === total ? 'cursor-not-allowed opacity-40' : ''
          }`}
        >
          →
        </button>
      </div>
    )
  }

  const thClass =
    'py-3.5 px-5 text-left font-rajdhani text-[11px] font-bold uppercase tracking-[1.4px] text-[rgba(255,255,255,0.5)] lg:text-[13px] lg:py-[18px]'
  const tdClass =
    'py-4 px-5 align-top font-rajdhani text-[14px] text-[rgba(255,255,255,0.78)] lg:text-[16px] lg:py-5'

  return (
    <>
      <AccountLayout>
        <div class="mb-4 mt-3 font-bebas text-[28px] leading-none tracking-[1.5px] text-white sm:mb-5 sm:mt-0 sm:text-[38px] lg:text-[48px]">
          Admin Subscriptions
        </div>

        <div
          class="overflow-hidden rounded-2xl border border-[rgba(138,43,226,0.15)]"
          style={{ background: 'rgba(16,12,30,0.6)' }}
        >
          <Show
            when={tableData().length > 0}
            fallback={
              <div class="py-16 text-center">
                <p class="font-bebas text-xl text-[rgba(255,255,255,0.4)]">
                  No subscriptions found.
                </p>
              </div>
            }
          >
            {/* Desktop table */}
            <div class="hidden overflow-x-auto sm:block">
              <table class="w-full border-collapse">
                <thead>
                  <tr
                    style={{
                      background: 'rgba(138,43,226,0.08)',
                      'border-bottom': '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <th class={thClass}>User</th>
                    <th
                      class={`${thClass} cursor-pointer select-none`}
                      onClick={() => sortData('characterId')}
                    >
                      <span class="inline-flex items-center gap-1.5">
                        Character {renderSortIndicator('characterId')}
                      </span>
                    </th>
                    <th
                      class={`${thClass} cursor-pointer select-none`}
                      onClick={() => sortData('startDate')}
                    >
                      <span class="inline-flex items-center gap-1.5">
                        Start Date {renderSortIndicator('startDate')}
                      </span>
                    </th>
                    <th
                      class={`${thClass} cursor-pointer select-none`}
                      onClick={() => sortData('endDate')}
                    >
                      <span class="inline-flex items-center gap-1.5">
                        End Date {renderSortIndicator('endDate')}
                      </span>
                    </th>
                    <th
                      class={`${thClass} cursor-pointer select-none`}
                      onClick={() => sortData('amount')}
                    >
                      <span class="inline-flex items-center gap-1.5">
                        Amount {renderSortIndicator('amount')}
                      </span>
                    </th>
                    <th
                      class={`${thClass} cursor-pointer select-none`}
                      onClick={() => sortData('tokens')}
                    >
                      <span class="inline-flex items-center gap-1.5">
                        Tokens {renderSortIndicator('tokens')}
                      </span>
                    </th>
                    <th
                      class={`${thClass} cursor-pointer select-none`}
                      onClick={() => sortData('status')}
                    >
                      <span class="inline-flex items-center gap-1.5">
                        Status {renderSortIndicator('status')}
                      </span>
                    </th>
                    <th class={thClass}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={tableData()}>{(sub) => renderTableRow(sub)}</For>
                </tbody>
              </table>
            </div>

            {/* Mobile stacked cards */}
            <div class="flex flex-col gap-3 p-4 sm:hidden">
              <For each={tableData()}>{(sub) => renderMobileCard(sub)}</For>
            </div>

            {renderPagination()}
          </Show>
        </div>
      </AccountLayout>
      <Footer />
      <CancellationModal
        show={showCancellationModal()}
        onClose={() => {
          setShowCancellationModal(false)
          setCancellingCharId(undefined)
        }}
        onConfirm={handleCancellationConfirm}
        characterName={cancellingCharId() ? characterFromId(cancellingCharId()!)?.name : undefined}
        loading={cancellingId() === cancellingCharId()}
      />
      <ReactivationModal
        show={showReactivateModal()}
        onClose={resetModal}
        onConfirm={confirmReactivation}
        loading={isReactivating()}
      />
    </>
  )
}
